import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronDown, ChevronUp, Check, AlertTriangle, X, Info, SearchX } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/* ============================================================
 * Helpers - formatting
 * ============================================================ */

const MONO = "ui-monospace, 'JetBrains Mono', SFMono-Regular, Menlo, monospace";

export const EM_DASH = "—";

export const fmtItalianDateTime = (iso) => {
  if (!iso) return EM_DASH;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return EM_DASH;
    const date = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${date} alle ${time}`;
  } catch {
    return EM_DASH;
  }
};

export const fmtItalianDate = (iso) => {
  if (!iso) return EM_DASH;
  try {
    // accetta "1980-01-01" puro o ISO completo
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    if (isNaN(d.getTime())) return EM_DASH;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return EM_DASH;
  }
};

export const orDash = (v) => {
  if (v === null || v === undefined || v === "") return EM_DASH;
  return v;
};

export const upperOrDash = (v) => {
  if (v === null || v === undefined || v === "") return EM_DASH;
  return String(v).toUpperCase();
};

/* ============================================================
 * BackLink
 * ============================================================ */

export const BackLink = ({ to = "/history", label = "Storico richieste", testId = "back-link" }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center gap-1.5 text-[13px] text-text-secondary hover:text-text-primary transition-colors mb-6"
  >
    <ChevronLeft size={14} strokeWidth={2} />
    {label}
  </Link>
);

/* ============================================================
 * HeroStatus - success / warning / danger / info
 * ============================================================ */

const HERO_VARIANTS = {
  success: {
    bg: "bg-success-subtle",
    iconBg: "bg-success",
    iconColor: "text-white",
    Icon: Check,
  },
  warning: {
    bg: "bg-warning-subtle",
    iconBg: "bg-warning",
    iconColor: "text-white",
    Icon: AlertTriangle,
  },
  danger: {
    bg: "bg-danger-subtle",
    iconBg: "bg-danger",
    iconColor: "text-white",
    Icon: X,
  },
  info: {
    bg: "bg-accent-subtle",
    iconBg: "bg-accent",
    iconColor: "text-white",
    Icon: Info,
  },
};

export const HeroStatus = ({ variant = "success", title, description, testId = "hero-status" }) => {
  const v = HERO_VARIANTS[variant] || HERO_VARIANTS.success;
  const Icon = v.Icon;
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-4 px-6 py-5 rounded-xl mb-6 ${v.bg}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${v.iconBg} ${v.iconColor}`}>
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div>
        <h2 className="text-[18px] font-semibold text-text-primary leading-tight">{title}</h2>
        {description && (
          <p className="text-sm text-text-secondary mt-1 leading-snug">{description}</p>
        )}
      </div>
    </div>
  );
};

/* ============================================================
 * Section + SectionLabel + Card
 * ============================================================ */

export const Section = ({ children, className = "" }) => (
  <div className={`mb-6 ${className}`}>{children}</div>
);

export const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted mb-3">
    {children}
  </div>
);

export const Card = ({ children, className = "", testId }) => (
  <div
    data-testid={testId}
    className={`bg-surface border border-border rounded-xl p-6 ${className}`}
  >
    {children}
  </div>
);

export const CardTitle = ({ children }) => (
  <div className="text-[15px] font-semibold text-text-primary mb-4 pb-3 border-b border-border">
    {children}
  </div>
);

/* ============================================================
 * EntityCard - card with subject identification
 * ============================================================ */

export const EntityCard = ({ title, entity, meta, sub, entitySize = "lg", testId = "entity-card" }) => {
  const entityCls = entitySize === "md"
    ? "text-[18px] font-semibold text-text-primary leading-tight"
    : "text-[24px] font-semibold text-text-primary leading-tight";
  return (
    <Card testId={testId}>
      {title && <CardTitle>{title}</CardTitle>}
      <div className={entityCls} data-testid="entity-name">
        {orDash(entity)}
      </div>
      {meta && (
        <div
          className="text-[13px] text-text-secondary mt-1"
          style={{ fontFamily: MONO }}
          data-testid="entity-meta"
        >
          {meta}
        </div>
      )}
      {sub && (
        <div className="text-sm text-text-secondary mt-1" data-testid="entity-sub">
          {sub}
        </div>
      )}
    </Card>
  );
};

/* ============================================================
 * StatCard - large number with label + optional detail
 * ============================================================ */

export const StatCard = ({ label, value, detail, detailColor = "default", muted = false, emptyDetail = false, testId }) => {
  const detailCls =
    detailColor === "success"
      ? "text-success font-medium"
      : detailColor === "danger"
      ? "text-danger font-medium"
      : emptyDetail
      ? "text-text-muted"
      : "text-text-secondary";
  return (
    <div
      data-testid={testId}
      className="bg-surface border border-border rounded-[10px] p-5"
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted mb-1.5">{label}</div>
      <div className={`text-[36px] font-bold leading-none mb-2 tabular-nums ${muted ? "text-text-muted" : "text-text-primary"}`}>
        {value}
      </div>
      {detail && <div className={`text-[13px] ${detailCls}`}>{detail}</div>}
    </div>
  );
};

/* ============================================================
 * DetailList - elenco righe con main/sub/count badge
 * ============================================================ */

export const DetailList = ({ items = [], testId = "detail-list" }) => (
  <div
    data-testid={testId}
    className="bg-surface border border-border rounded-[10px] overflow-hidden"
  >
    {items.length === 0 ? (
      <div className="px-5 py-6 text-center text-sm text-text-muted">Nessun dettaglio disponibile</div>
    ) : (
      items.map((it, i) => (
        <div
          key={it.key || i}
          data-testid={`detail-row-${i}`}
          className={`flex items-center justify-between px-5 py-4 hover:bg-subtle transition-colors ${
            i < items.length - 1 ? "border-b border-border" : ""
          }`}
        >
          <div className="min-w-0">
            <div className="text-[14px] font-medium text-text-primary truncate">{it.main}</div>
            {it.sub && <div className="text-xs text-text-secondary mt-0.5 truncate">{it.sub}</div>}
          </div>
          {it.count !== undefined && it.count !== null && (
            <div className="text-[13px] font-semibold text-text-secondary bg-subtle px-2.5 py-1 rounded-md flex-shrink-0 ml-3">
              {it.count}
            </div>
          )}
        </div>
      ))
    )}
  </div>
);

/* ============================================================
 * FooterActions - bottom action bar
 * ============================================================ */

export const FooterActions = ({ children, testId = "footer-actions" }) => (
  <div
    data-testid={testId}
    className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-border"
  >
    {children}
  </div>
);

export const ActionButton = ({
  variant = "primary",
  onClick,
  type = "button",
  icon: Icon,
  children,
  testId,
  disabled,
}) => {
  const styles = {
    primary: "bg-brand hover:bg-brand-hover text-white border border-brand",
    outline: "bg-surface hover:bg-subtle text-text-primary border border-border-strong",
    ghost: "bg-transparent hover:bg-subtle text-text-secondary border border-transparent",
    accent: "bg-accent hover:bg-accent-strong text-white border border-accent",
  }[variant] || "";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${styles}`}
    >
      {Icon && <Icon size={15} strokeWidth={1.75} />}
      {children}
    </button>
  );
};

/* ============================================================
 * TechDetailsToggle - admin-only raw JSON toggle
 * ============================================================ */

export const TechDetailsToggle = ({ data, testId = "tech-details-toggle" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Only admins and super_admins can see raw JSON. Operators must never see it.
  if (user?.role !== "admin" && user?.role !== "super_admin") return null;

  return (
    <div className="mt-8 pt-6" style={{ borderTop: "1px dashed var(--border)" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 text-text-muted hover:text-text-secondary text-xs font-medium transition-colors py-1"
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {open ? "Nascondi" : "Mostra"} dati tecnici (admin)
      </button>
      {open && (
        <pre
          data-testid="tech-details-json"
          className="mt-3 bg-subtle rounded-md p-4 text-[12px] overflow-auto max-h-[200px] leading-relaxed text-text-primary"
          style={{ fontFamily: MONO }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

/* ============================================================
 * StatsRow - 1..4 column stats grid
 * ============================================================ */

export const StatsRow = ({ columns = 2, children }) => {
  const cls =
    columns === 4
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      : columns === 3
      ? "grid grid-cols-1 sm:grid-cols-3 gap-4"
      : "grid grid-cols-1 sm:grid-cols-2 gap-4";
  return <div className={cls}>{children}</div>;
};

/* ============================================================
 * EmptyState - centered icon + title + description + CTA
 * ============================================================ */

export const EmptyState = ({
  icon: Icon = SearchX,
  title,
  description,
  primaryAction,
  testId = "empty-state",
}) => (
  <div
    data-testid={testId}
    className="bg-surface border border-border rounded-xl text-center"
    style={{ padding: "64px 24px" }}
  >
    <div
      className="inline-flex items-center justify-center bg-subtle text-text-muted mb-4"
      style={{ width: 56, height: 56, borderRadius: "50%" }}
    >
      <Icon size={26} strokeWidth={1.75} />
    </div>
    <div className="text-[18px] font-semibold text-text-primary mb-2">{title}</div>
    {description && (
      <div className="text-sm text-text-secondary mx-auto leading-relaxed mb-5" style={{ maxWidth: 380 }}>
        {description}
      </div>
    )}
    {primaryAction && (
      <button
        type="button"
        onClick={primaryAction.onClick}
        data-testid={`${testId}-primary-action`}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium bg-brand hover:bg-brand-hover text-white transition-all"
      >
        {primaryAction.label}
      </button>
    )}
  </div>
);

/* ============================================================
 * KvGrid / KvItem - clean label/value pairs
 * ============================================================ */

export const KvGrid = ({ columns = 2, children }) => (
  <div
    className="grid gap-4"
    style={{
      gridTemplateColumns: columns === 1 ? "1fr" : "1fr 1fr",
      columnGap: 32,
    }}
  >
    {children}
  </div>
);

export const KvItem = ({ label, value, mono = false, link, empty = false, columnSpan, valueColor, testId }) => {
  const baseValue = "text-[15px]";
  const colorCls =
    valueColor === "danger" ? "text-danger" :
    valueColor === "success" ? "text-success" :
    valueColor === "warning" ? "text-warning-strong" :
    null;
  const cls = empty
    ? `${baseValue} text-text-muted font-normal`
    : link
    ? `${baseValue} text-accent hover:underline font-medium`
    : colorCls
    ? `${baseValue} ${colorCls} font-medium`
    : `${baseValue} text-text-primary font-medium`;

  const valueNode = empty || value === null || value === undefined || value === "" ? EM_DASH : value;
  const spanStyle = columnSpan ? { gridColumn: "1 / -1" } : undefined;

  return (
    <div className="flex flex-col gap-1" style={spanStyle} data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      {link ? (
        <a href={typeof link === "string" ? link : "#"} className={cls} style={mono ? { fontFamily: MONO, fontSize: 14 } : undefined}>
          {valueNode}
        </a>
      ) : (
        <div className={cls} style={mono ? { fontFamily: MONO, fontSize: 14 } : undefined}>
          {valueNode}
        </div>
      )}
    </div>
  );
};

/* ============================================================
 * Divider - thin horizontal separator
 * ============================================================ */

export const Divider = () => (
  <div style={{ height: 1, background: "var(--border)", margin: "20px 0" }} />
);

/* ============================================================
 * Prose - body paragraph for explanatory text
 * ============================================================ */

export const Prose = ({ children }) => (
  <p className="text-[14px] text-text-secondary" style={{ lineHeight: 1.6 }}>{children}</p>
);

/* ============================================================
 * ProgressCard - centered spinner + title + description
 * ============================================================ */

export const ProgressCard = ({ title, description, testId = "progress-card" }) => (
  <div
    data-testid={testId}
    className="bg-surface border border-border rounded-xl text-center"
    style={{ padding: "40px 32px" }}
  >
    <div
      className="mx-auto mb-4"
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: "3px solid var(--bg-subtle)",
        borderTopColor: "var(--accent)",
        animation: "spin 1s linear infinite",
      }}
    />
    <div className="text-[17px] font-semibold text-text-primary mb-1.5">{title}</div>
    {description && <div className="text-sm text-text-secondary leading-relaxed">{description}</div>}
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/* ============================================================
 * timeAgo - localized Italian relative time
 * ============================================================ */

export const timeAgo = (iso) => {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return EM_DASH;
  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec} ${sec === 1 ? "secondo" : "secondi"} fa`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} ${min === 1 ? "minuto" : "minuti"} fa`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const days = Math.round(h / 24);
  return `${days} ${days === 1 ? "giorno" : "giorni"} fa`;
};

/* ============================================================
 * useNow - hook che tickka ogni N millis per refreshare counter
 * ============================================================ */

export const useNow = (intervalMs = 10000) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
};

/* ============================================================
 * SelectionTable - radio-list of items to pick one
 * ============================================================ */

export const SelectionTable = ({ items = [], selected, onSelect, testId = "selection-table" }) => (
  <div
    data-testid={testId}
    className="bg-surface border border-border rounded-[10px] overflow-hidden"
  >
    {items.length === 0 ? (
      <div className="px-5 py-8 text-sm text-text-muted text-center">Nessun risultato</div>
    ) : (
      items.map((it, i) => {
        const isSel = selected === it.id;
        return (
          <button
            type="button"
            key={it.id || i}
            data-testid={`selection-row-${i}`}
            aria-pressed={isSel}
            onClick={() => onSelect && onSelect(it.id)}
            className={`w-full text-left flex items-center gap-4 cursor-pointer transition-colors ${
              i < items.length - 1 ? "border-b border-border" : ""
            } ${isSel ? "bg-accent-subtle" : "hover:bg-subtle"}`}
            style={{
              padding: "16px 20px",
              paddingLeft: isSel ? 17 : 20,
              borderLeft: isSel ? "3px solid var(--accent)" : "none",
            }}
          >
            {/* radio */}
            <span
              className="flex-shrink-0"
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: isSel ? "2px solid var(--accent)" : "2px solid var(--border-strong)",
                background: "white",
                boxShadow: isSel ? "inset 0 0 0 4px var(--accent)" : "none",
                transition: "all 150ms",
              }}
            />

            {/* info */}
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-text-primary truncate">{it.main}</div>
              {it.sub && (
                <div className="text-[12px] text-text-secondary mt-0.5 truncate" style={{ fontFamily: MONO }}>
                  {it.sub}
                </div>
              )}
            </div>

            {/* right */}
            {(it.rightTop || it.rightBottom) && (
              <div className="text-right flex-shrink-0">
                {it.rightTop && (
                  <div className="text-[12px] font-medium text-text-secondary">{it.rightTop}</div>
                )}
                {it.rightBottom && (
                  <div className="text-[12px] text-text-secondary mt-0.5">{it.rightBottom}</div>
                )}
              </div>
            )}
          </button>
        );
      })
    )}
  </div>
);

/* ============================================================
 * Alert - inline notice (warning/info/success/danger)
 * ============================================================ */

const ALERT_VARIANTS = {
  warning: { bg: "bg-warning-subtle", color: "text-warning-strong", Icon: AlertTriangle },
  info: { bg: "bg-accent-subtle", color: "text-accent-strong", Icon: Info },
  success: { bg: "bg-success-subtle", color: "text-success-strong", Icon: Check },
  danger: { bg: "bg-danger-subtle", color: "text-danger-strong", Icon: X },
};

export const Alert = ({ variant = "info", icon: CustomIcon, children, testId = "alert" }) => {
  const v = ALERT_VARIANTS[variant] || ALERT_VARIANTS.info;
  const Icon = CustomIcon || v.Icon;
  return (
    <div
      data-testid={testId}
      className={`flex gap-3 rounded-[10px] ${v.bg} ${v.color}`}
      style={{ padding: 16 }}
    >
      <Icon size={18} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
      <div className="text-[13px] leading-relaxed">{children}</div>
    </div>
  );
};

/* ============================================================
 * PdfCard - file card with icon + meta + actions
 * ============================================================ */

export const PdfCard = ({ title, subject, sizeKB, generatedAt, onDownload, onPreview, testId = "pdf-card" }) => {
  const metaParts = [];
  if (subject) metaParts.push(subject);
  if (sizeKB !== null && sizeKB !== undefined && sizeKB !== "") metaParts.push(`${sizeKB} KB`);
  if (generatedAt) metaParts.push(`Generato il ${generatedAt}`);

  return (
    <div
      data-testid={testId}
      className="bg-surface border border-border rounded-xl flex flex-col sm:flex-row sm:items-center gap-5"
      style={{ padding: 32 }}
    >
      <div
        className="bg-accent-subtle text-accent-strong flex items-center justify-center flex-shrink-0"
        style={{ width: 56, height: 56, borderRadius: 10, fontFamily: "Inter", fontWeight: 600, fontSize: 14, letterSpacing: 0.5 }}
      >
        PDF
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[16px] font-semibold text-text-primary mb-1 truncate">{title}</div>
        <div className="text-[13px] text-text-secondary truncate">{metaParts.join(" · ") || EM_DASH}</div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {onPreview && (
          <button
            type="button"
            onClick={onPreview}
            data-testid="pdf-preview-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium bg-surface hover:bg-subtle text-text-primary border border-border-strong transition-all"
          >
            Anteprima
          </button>
        )}
        {onDownload && (
          <button
            type="button"
            onClick={onDownload}
            data-testid="pdf-download-btn"
            className="inline-flex items-center gap-1.5 rounded-md font-medium bg-brand hover:bg-brand-hover text-white transition-all"
            style={{ padding: "12px 20px", fontSize: 15 }}
          >
            Scarica PDF
          </button>
        )}
      </div>
    </div>
  );
};

/* ============================================================
 * Tabs + TabPanel
 * ============================================================ */

const TabsContext = React.createContext({ active: null });

export const Tabs = ({ defaultTab, tabs = [], onChange, children, testId = "tabs" }) => {
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const [active, setActive] = useState(defaultTab || (visibleTabs[0] && visibleTabs[0].id));

  // Reset se la tab attiva diventa nascosta
  useEffect(() => {
    if (!visibleTabs.find((t) => t.id === active) && visibleTabs[0]) {
      setActive(visibleTabs[0].id);
    }
    // eslint-disable-next-line
  }, [tabs.map((t) => `${t.id}:${t.hidden ? 1 : 0}`).join(",")]);

  const onSelect = (id) => {
    setActive(id);
    if (onChange) onChange(id);
  };

  return (
    <TabsContext.Provider value={{ active }}>
      <div
        data-testid={testId}
        className="flex border-b border-border mb-6"
        style={{ gap: 0 }}
        role="tablist"
      >
        {visibleTabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(t.id)}
              data-testid={`tab-${t.id}`}
              className={`text-[14px] font-medium transition-colors`}
              style={{
                padding: "12px 16px",
                color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                borderBottom: isActive ? "2px solid var(--primary)" : "2px solid transparent",
                marginBottom: -1,
                background: "transparent",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {children}
    </TabsContext.Provider>
  );
};

export const TabPanel = ({ id, children }) => {
  const { active } = React.useContext(TabsContext);
  if (active !== id) return null;
  return <div data-testid={`tab-panel-${id}`}>{children}</div>;
};
